(() => {
  const modalSelector = '.kiwi-sizing-modal';
  const messageSelector = `${modalSelector} .ks-html-content`;
  const tableSelector = '.ks-table';
  const cellSelector = '.ks-table-cell';
  const emptyClass = 'cw-kiwi-message-empty';
  const fixedAttribute = 'data-cw-column-hover-fix';

  const setImportant = (element, property, value) => {
    if (!element) return;

    const currentValue = element.style.getPropertyValue(property);
    const currentPriority = element.style.getPropertyPriority(property);

    if (currentValue === value && currentPriority === 'important') return;
    element.style.setProperty(property, value, 'important');
  };

  const updateMessage = (message) => {
    const text = (message.innerText || message.textContent || '')
      .replace(/[\u00a0\u200B-\u200D\u2060\uFEFF]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const hasReadableContent = /[\p{L}\p{N}]/u.test(text);

    message.classList.toggle(emptyClass, !hasReadableContent);

    if (hasReadableContent) {
      message.style.removeProperty('display');
      message.removeAttribute('aria-hidden');
    } else {
      message.style.setProperty('display', 'none', 'important');
      message.setAttribute('aria-hidden', 'true');
    }
  };

  const normalizeTable = (table, isMobile = false) => {
    setImportant(table, 'width', '100%');
    setImportant(table, 'min-width', '0');
    setImportant(table, 'max-width', '100%');
    setImportant(table, 'height', 'auto');
    setImportant(table, 'min-height', '0');
    setImportant(table, 'max-height', 'none');
    setImportant(table, 'table-layout', 'auto');
    setImportant(table, 'font-family', 'Inter, Arial, sans-serif');

    const rows = Array.from(table.querySelectorAll('.ks-table-row, tr'));
    const rowData = rows.map((row) => {
      const cells = Array.from(row.children).filter((cell) =>
        cell.matches('.ks-table-cell, th, td')
      );
      const spans = cells.map((cell) => {
        const colspan = Number.parseInt(cell.getAttribute('colspan') || '1', 10);
        return Number.isFinite(colspan) && colspan > 0 ? colspan : 1;
      });

      return {
        row,
        cells,
        spans,
        columns: spans.reduce((total, span) => total + span, 0)
      };
    }).filter(({ cells }) => cells.length);

    if (!rowData.length) return;

    const isDataTable = rowData.length > 1;
    const maxColumns = Math.max(...rowData.map(({ columns }) => columns), 1);

    rowData.forEach(({ row, cells, spans, columns }, rowIndex) => {
      const isSingleCellRow = !isDataTable && cells.length === 1;
      const isContentRow = !isDataTable && cells.length > 1;
      const verticalPadding = isContentRow
        ? (isMobile ? '12px' : '14px')
        : isSingleCellRow
          ? (isMobile ? '6px' : '7px')
          : '8px';

      const rowMinHeight = isDataTable
        ? (rowIndex === 0
            ? '38px'
            : isMobile
              ? '43px'
              : rowIndex === 1
                ? '42px'
                : '44px')
        : '0';

      setImportant(row, 'width', '100%');
      setImportant(row, 'height', 'auto');
      setImportant(row, 'min-height', rowMinHeight);
      setImportant(row, 'max-height', 'none');

      cells.forEach((cell, index) => {
        if (isDataTable) {
          const missingColumns = Math.max(0, maxColumns - columns);
          const span = spans[index] + (index === cells.length - 1 ? missingColumns : 0);
          const width = `${(span / maxColumns) * 100}%`;

          setImportant(cell, 'width', width);
          setImportant(cell, 'flex', `0 1 ${width}`);
        } else if (cells.length === 1) {
          setImportant(cell, 'width', '100%');
          setImportant(cell, 'flex', '1 1 100%');
        } else {
          setImportant(cell, 'width', 'auto');
          setImportant(cell, 'flex', '1 1 auto');
        }

        setImportant(cell, 'min-width', '0');
        setImportant(cell, 'max-width', 'none');
        setImportant(cell, 'box-sizing', 'border-box');
        setImportant(cell, 'font-family', 'Inter, Arial, sans-serif');
        setImportant(cell, 'font-style', 'normal');
        setImportant(cell, 'height', 'auto');
        setImportant(cell, 'min-height', '0');
        setImportant(cell, 'max-height', 'none');
        setImportant(cell, 'white-space', 'normal');
        setImportant(cell, 'overflow-wrap', 'anywhere');
        setImportant(cell, 'word-break', 'normal');
        setImportant(cell, 'padding-top', verticalPadding);
        setImportant(cell, 'padding-bottom', verticalPadding);

        if (isMobile && isDataTable) {
          setImportant(cell, 'padding-left', '4px');
          setImportant(cell, 'padding-right', '4px');
          setImportant(cell, 'border-top', '0');
          setImportant(cell, 'border-left', '0');
          setImportant(cell, 'border-right', '1px solid rgba(255, 255, 255, 0.065)');
          setImportant(cell, 'border-bottom', '1px solid rgba(255, 255, 255, 0.065)');
          setImportant(cell, 'outline', '0');
          setImportant(cell, 'box-shadow', 'none');
        }

        cell.querySelectorAll('*').forEach((node) => {
          setImportant(node, 'height', 'auto');
          setImportant(node, 'min-height', '0');
          setImportant(node, 'max-height', 'none');
          setImportant(node, 'max-width', '100%');
          setImportant(node, 'white-space', 'normal');
          setImportant(node, 'overflow-wrap', 'anywhere');
          setImportant(node, 'word-break', 'normal');
        });

        cell.querySelectorAll('ol, ul').forEach((list) => {
          setImportant(list, 'margin-top', '0');
          setImportant(list, 'margin-bottom', '0');
          setImportant(list, 'padding-top', '0');
          setImportant(list, 'padding-bottom', '0');
          setImportant(list, 'padding-inline-start', '1.4em');
        });

        if (isDataTable && index === cells.length - 1) {
          setImportant(cell, 'border-right', '0');
        }
      });
    });

    if (isMobile && isDataTable) {
      rowData.forEach(({ row }) => {
        setImportant(row, 'border', '0');
        setImportant(row, 'outline', '0');
        setImportant(row, 'box-shadow', 'none');
      });

      rowData[0].cells.forEach((cell) => {
        setImportant(cell, 'text-align', 'center');
        cell.querySelectorAll('*').forEach((node) => {
          setImportant(node, 'text-align', 'center');
        });
      });

      rowData.slice(1).forEach(({ cells }) => {
        cells.forEach((cell, index) => {
          const color = index === 0
            ? 'rgba(255, 255, 255, 0.96)'
            : 'rgba(255, 255, 255, 0.9)';
          const fontWeight = index === 0 ? '500' : '400';

          setImportant(cell, 'background', 'rgba(255, 255, 255, 0.075)');
          setImportant(cell, 'background-color', 'rgba(255, 255, 255, 0.075)');
          setImportant(cell, 'color', color);
          setImportant(cell, 'font-size', '12px');
          setImportant(cell, 'font-weight', fontWeight);
          setImportant(cell, 'letter-spacing', '0');

          cell.querySelectorAll('*').forEach((node) => {
            setImportant(node, 'background', 'transparent');
            setImportant(node, 'background-color', 'transparent');
            setImportant(node, 'color', color);
            setImportant(node, 'font-size', '12px');
            setImportant(node, 'font-weight', fontWeight);
            setImportant(node, 'letter-spacing', '0');
          });
        });
      });

      rowData[rowData.length - 1].cells.forEach((cell) => {
        setImportant(cell, 'border-bottom', '0');
      });
    }

    if (isDataTable && !isMobile) {
      const firstCell = rowData[0].cells[0];

      if (firstCell) {
        setImportant(firstCell, 'padding-left', '0');
        setImportant(firstCell, 'padding-right', '0');
        setImportant(firstCell, 'text-align', 'center');
        firstCell.querySelectorAll('*').forEach((node) => {
          setImportant(node, 'text-align', 'center');
        });
      }
    }
  };

  const normalizeModal = (modal) => {
    const isMobile = window.matchMedia('(max-width: 599px)').matches;
    const inner = modal.querySelector('.kiwi-sizing-modal-inner');
    const content = modal.querySelector('.ks-modal-content');
    const messages = modal.querySelectorAll('.ks-html-content');
    const wrappers = modal.querySelectorAll('.ks-table-wrapper');
    const tables = modal.querySelectorAll(tableSelector);

    if (inner) {
      setImportant(inner, 'position', 'fixed');
      setImportant(inner, 'width', isMobile ? '100%' : 'min(720px, calc(100vw - 56px))');
      setImportant(inner, 'max-width', isMobile ? 'none' : 'min(720px, calc(100vw - 56px))');
      setImportant(inner, 'height', isMobile ? 'calc(100dvh - 8px)' : 'auto');
      setImportant(inner, 'min-height', isMobile ? 'calc(100dvh - 8px)' : '0');
      setImportant(inner, 'max-height', isMobile ? 'calc(100dvh - 8px)' : 'calc(100dvh - 40px)');
      setImportant(inner, 'overflow', 'hidden');
      setImportant(inner, 'box-sizing', 'border-box');
      setImportant(inner, 'margin', '0');

      if (isMobile) {
        setImportant(inner, 'top', '8px');
        setImportant(inner, 'left', '0');
        setImportant(inner, 'right', '0');
        setImportant(inner, 'bottom', '0');
        setImportant(inner, 'transform', 'none');
        setImportant(inner, 'display', 'flex');
        setImportant(inner, 'flex-direction', 'column');
      } else {
        setImportant(inner, 'top', '50%');
        setImportant(inner, 'left', '50%');
        setImportant(inner, 'right', 'auto');
        setImportant(inner, 'bottom', 'auto');
        setImportant(inner, 'transform', 'translate(-50%, -50%)');
        setImportant(inner, 'display', 'block');
      }
    }

    if (content) {
      setImportant(content, 'width', '100%');
      setImportant(content, 'height', 'auto');
      setImportant(content, 'min-height', '0');
      setImportant(content, 'max-height', isMobile ? 'none' : 'calc(100dvh - 120px)');
      setImportant(content, 'overflow-x', 'hidden');
      setImportant(content, 'overflow-y', 'auto');
      setImportant(content, 'background', 'transparent');
      setImportant(content, 'box-sizing', 'border-box');
      setImportant(content, 'font-family', 'Inter, Arial, sans-serif');
      setImportant(content, 'overscroll-behavior', 'contain');
      setImportant(content, '-webkit-overflow-scrolling', 'touch');
      setImportant(content, 'flex', isMobile ? '1 1 auto' : '0 1 auto');

      if (isMobile) {
        setImportant(content, 'padding', '15px 12px max(10px, env(safe-area-inset-bottom))');
      } else {
        setImportant(content, 'padding', '26px 30px 14px');
      }
    }

    messages.forEach((message) => {
      const isEmptyMessage = message.classList.contains(emptyClass);

      if (isEmptyMessage) {
        setImportant(message, 'display', 'none');
        setImportant(message, 'width', '0');
        setImportant(message, 'height', '0');
        setImportant(message, 'min-width', '0');
        setImportant(message, 'min-height', '0');
        setImportant(message, 'max-width', '0');
        setImportant(message, 'max-height', '0');
        setImportant(message, 'margin', '0');
        setImportant(message, 'padding', '0');
        setImportant(message, 'overflow', 'hidden');
        setImportant(message, 'border', '0');
        setImportant(message, 'box-shadow', 'none');
        setImportant(message, 'opacity', '0');
        setImportant(message, 'visibility', 'hidden');
        return;
      }

      setImportant(message, 'display', 'block');
      setImportant(message, 'width', '100%');
      setImportant(message, 'height', 'auto');
      setImportant(message, 'min-width', '0');
      setImportant(message, 'min-height', '0');
      setImportant(message, 'max-width', 'none');
      setImportant(message, 'max-height', 'none');
      setImportant(message, 'font-family', 'Inter, Arial, sans-serif');
      setImportant(message, 'font-style', 'normal');
      setImportant(message, 'font-weight', '600');
      setImportant(message, 'box-sizing', 'border-box');
      setImportant(message, 'text-align', 'left');
      setImportant(message, 'opacity', '1');
      setImportant(message, 'visibility', 'visible');
      setImportant(message, 'white-space', 'normal');
      setImportant(message, 'overflow-wrap', 'anywhere');
      setImportant(message, 'word-break', 'normal');
      setImportant(message, 'margin-top', '4px');
      setImportant(message, 'margin-right', '0');
      setImportant(message, 'margin-left', '0');

      if (isMobile) {
        setImportant(message, 'font-size', '13px');
        setImportant(message, 'line-height', '18px');
        setImportant(message, 'padding', '6px 12px');
        setImportant(message, 'margin-bottom', '4px');
      } else {
        setImportant(message, 'font-size', '13px');
        setImportant(message, 'line-height', '1.45');
        setImportant(message, 'padding', '7px 16px');
        setImportant(message, 'margin-bottom', '4px');
      }

      message.querySelectorAll('*').forEach((node) => {
        setImportant(node, 'font-weight', '600');
        setImportant(node, 'height', 'auto');
        setImportant(node, 'min-height', '0');
        setImportant(node, 'max-height', 'none');
        setImportant(node, 'max-width', '100%');
        setImportant(node, 'white-space', 'normal');
        setImportant(node, 'overflow-wrap', 'anywhere');
        setImportant(node, 'word-break', 'normal');
      });

      message.querySelectorAll('*').forEach((node) => {
        setImportant(node, 'margin-top', '0');
        setImportant(node, 'margin-bottom', '0');
        setImportant(node, 'padding-top', '0');
        setImportant(node, 'padding-bottom', '0');
      });

      message.querySelectorAll('div, section').forEach((node) => {
        const nodeText = (node.innerText || node.textContent || '')
          .replace(/[\u00a0\u200B-\u200D\u2060\uFEFF]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        const hasNodeContent = /[\p{L}\p{N}]/u.test(nodeText) || node.querySelector('img, picture, svg, video');

        if (!hasNodeContent) {
          setImportant(node, 'display', 'none');
        }
      });
    });

    wrappers.forEach((wrapper) => {
      setImportant(wrapper, 'width', '100%');
      setImportant(wrapper, 'height', 'auto');
      setImportant(wrapper, 'min-height', '0');
      setImportant(wrapper, 'max-height', 'none');
      setImportant(wrapper, 'overflow-x', 'auto');
      setImportant(wrapper, 'overflow-y', 'visible');
      setImportant(wrapper, 'box-sizing', 'border-box');

      const wrapperTables = Array.from(wrapper.querySelectorAll(tableSelector));
      const wrapperRows = wrapperTables.flatMap((table) =>
        Array.from(table.querySelectorAll('.ks-table-row, tr'))
      );
      const wrapperCells = wrapperRows.flatMap((row) =>
        Array.from(row.children).filter((cell) => cell.matches('.ks-table-cell, th, td'))
      );
      const isCompactSingleRow = wrapperRows.length === 1 && wrapperCells.length === 1;

      setImportant(wrapper, 'margin-top', isCompactSingleRow ? '4px' : '0');
      setImportant(wrapper, 'margin-bottom', isCompactSingleRow ? '4px' : '0');

      if (isMobile) {
        setImportant(wrapper, 'border', '1px solid rgba(255, 255, 255, 0.085)');
        setImportant(wrapper, 'outline', '0');
        setImportant(wrapper, 'box-shadow', 'none');
      }
    });

    tables.forEach((table) => {
      normalizeTable(table, isMobile);
    });
  };

  const updateAll = () => {
    document.querySelectorAll(messageSelector).forEach(updateMessage);
    document.querySelectorAll(modalSelector).forEach(normalizeModal);
  };

  const scheduleChecks = () => {
    window.clearTimeout(window.cwKiwiMessageCheck1);
    window.clearTimeout(window.cwKiwiMessageCheck2);
    window.clearTimeout(window.cwKiwiMessageCheck3);
    window.clearTimeout(window.cwKiwiMessageCheck4);

    window.cwKiwiMessageCheck1 = window.setTimeout(updateAll, 0);
    window.cwKiwiMessageCheck2 = window.setTimeout(updateAll, 150);
    window.cwKiwiMessageCheck3 = window.setTimeout(updateAll, 500);
    window.cwKiwiMessageCheck4 = window.setTimeout(updateAll, 1000);
  };

  window.cwKiwiMessageObserver?.disconnect();
  window.cwKiwiMessageObserver = new MutationObserver(updateAll);
  window.cwKiwiMessageObserver.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true
  });

  updateAll();

  document.addEventListener('click', (event) => {
    if (!event.target.closest('#KiwiSizingChart, .kiwi-sizing-modal')) return;
    scheduleChecks();
  }, true);

  window.addEventListener('resize', scheduleChecks, { passive: true });
  window.addEventListener('orientationchange', scheduleChecks, { passive: true });
  window.visualViewport?.addEventListener('resize', scheduleChecks, { passive: true });

  const clearColumnFix = (table) => {
    table.querySelectorAll(`[${fixedAttribute}]`).forEach((cell) => {
      cell.style.removeProperty('background');
      cell.style.removeProperty('background-color');
      cell.style.removeProperty('color');
      cell.removeAttribute(fixedAttribute);
    });
  };

  const isOpaqueWhite = (color) => {
    const values = color.match(/[\d.]+/g)?.map(Number);
    if (!values || values.length < 3) return false;

    const [red, green, blue, alpha = 1] = values;
    return alpha >= 0.65 && red >= 225 && green >= 225 && blue >= 225;
  };

  const softenColumnHover = (table) => {
    table.querySelectorAll(cellSelector).forEach((cell) => {
      if (!isOpaqueWhite(getComputedStyle(cell).backgroundColor)) return;

      cell.setAttribute(fixedAttribute, '');
      cell.style.setProperty('background-color', 'rgba(255, 255, 255, 0.12)', 'important');
      cell.style.setProperty('color', 'rgba(255, 255, 255, 0.96)', 'important');
    });
  };

  document.addEventListener('mouseover', (event) => {
    if (!window.matchMedia('(hover: hover)').matches) return;

    const table = event.target.closest(`${modalSelector} ${tableSelector}`);
    if (!table) return;

    clearColumnFix(table);
    window.requestAnimationFrame(() => softenColumnHover(table));
  }, true);

  document.addEventListener('mouseout', (event) => {
    const table = event.target.closest(`${modalSelector} ${tableSelector}`);
    if (!table || table.contains(event.relatedTarget)) return;

    clearColumnFix(table);
  }, true);
})();
